<template>
  <div
    class="ChatHistoryItem"
    :data-editing="isEditMode"
    @contextmenu="openContextMenu"
  >
    <UIForm
      v-if="isEditMode"
      class="ChatHistoryItem__form"
      :submitOnEnter="true"
      @submit.prevent="onSubmit"
      @click.prevent=""
      @enterPressed="onSubmit"
    >
      <UIInputText
        ref="inputRef"
        v-model="title"
        name="title"
        isSanitised
        class="ChatHistoryItem__input"
      />

      <!-- Renaming action buttons -->
      <div class="ChatHistoryItem__rename-actions">
        <UIButton
          class="ChatHistoryItem__rename-action-icon"
          type="submit"
          leadIcon="checkRegular"
          aria-label="Save name"
          theme="tertiary"
          size="sm"
          @click.stop=""
        />
        <UIButton
          class="ChatHistoryItem__rename-action-icon"
          leadIcon="close"
          aria-label="Cancel rename"
          theme="tertiary"
          size="sm"
          @click="cancelRename()"
        />
      </div>
    </UIForm>

    <TheSidebarMenuItem
      v-else
      :isActive="isActive"
      :title="chat.title"
      class="ChatHistoryItem__menu-item"
    >
      <template v-if="isPinned" #icon>
        <div class="ChatHistoryItem__leading">
          <UIIcon name="thumbtackSolid" class="ChatHistoryItem__pin-icon" />
          <span class="ChatHistoryItem__drag-handle" aria-hidden="true">
            <UIIcon name="gripDotsVerticalRegular" />
          </span>
        </div>
      </template>

      <template #label>
        {{ chat.title }}
      </template>

      <template
        v-if="isExpiring || (!isNotAuthorised && showContextMenu)"
        #contextMenu
      >
        <div class="ChatHistoryItem__trailing">
          <UIBadge
            v-if="isExpiring"
            class="ChatHistoryItem__expiring-badge"
            theme="warning"
            hierarchy="secondary"
            size="md"
            icon="calendarTime"
          />

          <UIDropdown
            v-if="!isNotAuthorised && showContextMenu"
            :items="chatOptions"
            placement="bottom-end"
            @click.capture.prevent=""
          >
            <template #toggle-container="{ isOpen }">
              <UIButton
                ref="contextMenuToggleRef"
                class="ChatHistoryItem__more"
                :data-open="isOpen"
                leadIcon="dotsHorizontal"
                aria-label="Chat options"
                theme="tertiary"
                size="sm"
              />
            </template>
          </UIDropdown>
        </div>
      </template>
    </TheSidebarMenuItem>
  </div>

  <ModalDeleteChat
    v-bind="deleteChatModal"
    :isLoading="isDeleting"
    @close="deleteChatModal.close()"
    @confirm="submitDelete()"
  />
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick } from 'vue'

import {
  type IIcons,
  UIBadge,
  UIButton,
  UIDropdown,
  UIForm,
  UIIcon,
  UIInputText,
} from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { useRoute, useRouter } from 'vue-router'
import { string } from 'yup'

import { PINNED_CHATS_LIMIT_MESSAGE } from '@/apiClient/queries/chatPinCacheUtils'
import { useChatQueries } from '@/apiClient/queries/useChatQueries'
import type { IChatListItem } from '@/apiClient/types/chat'
import { useModal } from '@/composables/useModal'
import { useAppStore } from '@/store/app'
import { useChatStore } from '@/store/chat'
import { useToast } from '@/store/toast'

import TheSidebarMenuItem from './TheSidebarMenuItem.vue'
import ModalDeleteChat from '../../modals/ModalDeleteChat.vue'

const props = withDefaults(
  defineProps<{
    chat: IChatListItem
    isActive?: boolean
    showContextMenu?: boolean
    /** Renders the amber "expiring soon" badge next to the chat title. */
    isExpiring?: boolean
  }>(),
  {
    isActive: false,
    showContextMenu: true,
    isExpiring: false,
  },
)

const { update, remove } = useChatQueries()

const { isNotAuthorisedPage: isNotAuthorised } = storeToRefs(useAppStore())

const chatStore = useChatStore()

// #region Pin
const { hasReachedPinnedChatsLimit, isChatPinningEnabled } =
  storeToRefs(chatStore)
const isPinned = computed(() => chatStore.isChatPinned(props.chat))

/**
 * Pins / unpins the chat, moving it between the sidebar's "Pinned" section and its
 * dated list. The store owns the list itself — the API replaces it wholesale, so
 * building the next one per row is what lets two quick clicks drop each other's pin.
 *
 * The cap is checked here rather than in the store because this is where it can be
 * explained; the store's write is optimistic, so a second click already sees the first.
 */
const toggleChatPin = () => {
  const nextPinned = !isPinned.value
  if (nextPinned && hasReachedPinnedChatsLimit.value) {
    useToast().onError(PINNED_CHATS_LIMIT_MESSAGE)
    return
  }

  // The sidebar has already moved the row, so the outcome is reported now; a failed
  // request is toasted (and rolled back) by the mutation itself.
  chatStore.setChatPinned(props.chat, nextPinned).catch(() => undefined)
  useToast().onSuccess(
    nextPinned ? 'Chat pinned to the top of the sidebar.' : 'Chat unpinned.',
  )
}
// #endregion

// #region Context menu
const contextMenuToggleRef = ref<InstanceType<typeof UIButton> | null>(null)
/**
 * Right-clicking the row opens the same actions as the "..." toggle. The dropdown is a
 * click-toggled popover, so the native menu is suppressed and the toggle clicked
 * on the user's behalf rather than duplicating the menu behind a second trigger.
 */
const openContextMenu = (event: MouseEvent) => {
  const toggleElement = contextMenuToggleRef.value?.$el as
    HTMLElement | undefined
  if (!toggleElement) return

  event.preventDefault()
  toggleElement.click()
}
// #endregion

// #region Rename
const isEditMode = ref(false)
const title = ref('')
const inputRef = ref<InstanceType<typeof UIInputText> | null>(null)

watch(
  () => props.chat,
  (_chat) => {
    title.value = _chat.title
  },
  { immediate: true },
)

// The schema and the field live together on purpose: they are shaped by this one
// template, so they stay here rather than moving into a composable.
const { handleSubmit } = useForm<{ title: string }>({
  validationSchema: {
    title: string()
      .trim()
      .max(80, 'Title must be less than 80 characters')
      .required('You must provide a name'),
  },
})

const startRename = () => {
  isEditMode.value = true
  nextTick(() => {
    inputRef.value?.$el.querySelector('input')?.focus()
  })
}

const cancelRename = () => {
  isEditMode.value = false
  title.value = props.chat.title
}

const isSaving = ref(false)
const onSubmit = handleSubmit((values) => {
  if (isSaving.value) return

  isSaving.value = true

  update
    .mutateAsync({
      chatId: props.chat.id,
      chat: { title: values.title },
    })
    .then(() => {
      useToast().onSuccess('Title updated.')
    })
    .catch(() => {
      title.value = props.chat.title
    })
    .finally(() => {
      isEditMode.value = false
      isSaving.value = false
    })
})
// #endregion

// #region Delete
const route = useRoute()
const router = useRouter()
const deleteChatModal = useModal<IChatListItem>()

const isDeleting = ref(false)
const submitDelete = () => {
  if (isDeleting.value) return

  isDeleting.value = true

  remove
    .mutateAsync(props.chat.id)
    .then(() => {
      useToast().onSuccess('Chat deleted.')
      // Deleting the chat you are reading leaves nothing to show.
      if (route.params.id === props.chat.id) {
        router.push({ name: 'NewChat' })
      }
    })
    .finally(() => {
      deleteChatModal.close()
      isDeleting.value = false
    })
}
// #endregion

// #region Menu options
type IChatOption = {
  label: string
  value: string
  onClick: () => void
  icon: IIcons
  isDestructive?: boolean
}

const pinChatOption = computed<IChatOption[]>(() =>
  isChatPinningEnabled.value
    ? [
        {
          label: isPinned.value ? 'Unpin' : 'Pin',
          value: 'pin',
          onClick: () => toggleChatPin(),
          icon: 'thumbtackSolid',
        },
      ]
    : [],
)

const chatOptions = computed<IChatOption[]>(() => [
  // Pinning is behind a kill switch; with it off a row keeps rename and delete.
  ...pinChatOption.value,
  {
    label: 'Rename',
    value: 'rename',
    onClick: () => startRename(),
    icon: 'pencil',
  },
  {
    label: 'Delete',
    value: 'delete',
    onClick: () => deleteChatModal.open(props.chat),
    icon: 'trashRegular',
    isDestructive: true,
  },
])
// #endregion
</script>

<style scoped lang="scss">
.ChatHistoryItem {
  @include font-body-small-regular;
  cursor: pointer;
  display: flex;
  align-items: center;

  &[data-editing='true'] {
    padding: 0.5rem 0.75rem 0.5rem 0.5rem;
  }

  .ChatHistoryItem__menu-item {
    width: 100%;
  }

  .ChatHistoryItem__form {
    width: 100%;
    display: flex;
    gap: 0.5rem;
  }

  .ChatHistoryItem__trailing {
    display: flex;
    align-items: center;
    gap: var(---fds-global-space-xs);
  }

  .ChatHistoryItem__expiring-badge {
    flex-shrink: 0;
  }

  // Pin marker for a pinned row. The drag handle sits on top of it and is revealed by
  // the pinned section on hover (see PinnedChatHistory), so the two never take space
  // from each other and the label never shifts.
  .ChatHistoryItem__leading {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  .ChatHistoryItem__pin-icon {
    --size: 0.875rem;
    transition: opacity 0.2s ease-in-out;
  }

  .ChatHistoryItem__drag-handle {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    z-index: 1;

    --size: 1rem;
    --color: var(--icon-primary);
  }

  // Collapsed (zero-width, faded out) at rest; expands and fades in on row hover
  // (see ChatHistoryLink) or while its dropdown is open. Animating `width` rather
  // than toggling `display` eases the icon in and lets the neighbouring expiring
  // badge slide aside smoothly as the dropdown's inline-block wrapper grows. The
  // button is clipped with `overflow` while collapsed.
  .ChatHistoryItem__more {
    width: 0;
    // ui-lib UIButton pads 4px (border-box), which would keep 8px visible at width 0.
    padding: 0 !important;
    opacity: 0;
    overflow: hidden;
    transition:
      width 0.2s ease-in-out,
      opacity 0.2s ease-in-out;

    &[data-open='true'] {
      width: 1.75rem;
      opacity: 1;
    }
  }

  .ChatHistoryItem__input {
    width: 13rem;
  }

  .ChatHistoryItem__rename-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
  }
}
</style>

<style>
/* Press state: parent <a> receives :active — include :hover so press beats hover */
a:active .ChatHistoryItem .TheSidebarMenuItem:not([data-is-active='true']),
a:active:hover
  .ChatHistoryItem
  .TheSidebarMenuItem:not([data-is-active='true']) {
  background-color: var(--interactive-bg-accent-press);
  color: var(--text-accent);
  font-weight: 500;
}

a:active .ChatHistoryItem .TheSidebarMenuItem[data-is-active='true'],
a:active:hover .ChatHistoryItem .TheSidebarMenuItem[data-is-active='true'] {
  background-color: var(--interactive-bg-accent-press);
}
</style>
